/**
 * Minimal LaTeX → Token-list parser.
 *
 * Scope (deliberately small): the subset needed to round-trip the
 * library catalog formulas so loading a system-seeded entry doesn't
 * collapse to a single fake-letter token in the visual editor.
 *
 * Supported:
 *   - atomic chars: a-z A-Z 0-9 + - = ( ) [ ] ., punctuation
 *   - `\command`     → palette lookup (Greek, ops, rels, arrows, fns,
 *                       set glyphs, infinity, bmod, mid, sim, ldots…)
 *   - `\frac{a}{b}`  → struct frac with body slots filled
 *   - `\sqrt{x}`     → struct sqrt with body filled
 *   - `\sqrt[n]{x}`  → struct nthroot
 *   - `x^{a}`, `x_{i}`, `x_{i}^{n}` → subsup attached to preceding atom
 *   - `\int_{a}^{b} body`, `\sum_{a}^{b} body`, `\prod` → bigop atom
 *   - `\left( … \right)` / `\left| … \right|` → parens / abs structs
 *
 * Out of scope: matrices, cases, aligned, accents (\hat, \vec,
 * \overline), spacing macros (\,), `\text{…}`, `\operatorname{…}`.
 * Anything we can't parse falls through to a single-letter token
 * carrying the raw remaining LaTeX, and the caller can react.
 *
 * The parser returns `{ tokens, ok }`. `ok=false` means the LaTeX
 * contained constructs we don't model yet — the editor's host can
 * keep the tokens (they cover the parseable prefix) but should
 * surface the LaTeX source view rather than pretend the visual
 * editor is in sync.
 */
import { T, type Token } from "./tokens";

interface ParseResult {
  tokens: Token[];
  ok: boolean;
  /** First unparsed substring, for diagnostics. */
  unparsed?: string;
}

// Greek letter command → glyph. Lowercase + uppercase.
const GREEK: Record<string, string> = {
  alpha: "α", beta: "β", gamma: "γ", delta: "δ", epsilon: "ε", varepsilon: "ε",
  zeta: "ζ", eta: "η", theta: "θ", vartheta: "θ", iota: "ι", kappa: "κ",
  lambda: "λ", mu: "μ", nu: "ν", xi: "ξ", omicron: "ο", pi: "π", varpi: "π",
  rho: "ρ", varrho: "ρ", sigma: "σ", varsigma: "σ", tau: "τ", upsilon: "υ",
  phi: "φ", varphi: "φ", chi: "χ", psi: "ψ", omega: "ω",
  Gamma: "Γ", Delta: "Δ", Theta: "Θ", Lambda: "Λ", Xi: "Ξ", Pi: "Π",
  Sigma: "Σ", Upsilon: "Υ", Phi: "Φ", Psi: "Ψ", Omega: "Ω",
};

// Operators and similar atoms with explicit Unicode rendering.
const OPS: Record<string, string> = {
  times: "×", div: "÷", cdot: "⋅", pm: "±", mp: "∓",
  oplus: "⊕", otimes: "⊗", odot: "⊙", ast: "∗", circ: "∘", star: "⋆",
  partial: "∂", nabla: "∇", infty: "∞", hbar: "ℏ",
  cup: "∪", cap: "∩", setminus: "∖", emptyset: "∅",
  bmod: " mod ", pmod: " mod ", mid: "|",
};

const RELS: Record<string, string> = {
  neq: "≠", approx: "≈", equiv: "≡", sim: "∼", simeq: "≃", cong: "≅",
  propto: "∝", leq: "≤", le: "≤", geq: "≥", ge: "≥", ll: "≪", gg: "≫",
  subset: "⊂", subseteq: "⊆", supset: "⊃", supseteq: "⊇",
  in: "∈", notin: "∉", ni: "∋",
};

const ARROWS: Record<string, string> = {
  to: "→", rightarrow: "→", leftarrow: "←", Rightarrow: "⇒", Leftarrow: "⇐",
  Leftrightarrow: "⇔", iff: "⇔", leftrightarrow: "↔", mapsto: "↦",
  longrightarrow: "⟶", longleftarrow: "⟵", hookrightarrow: "↪",
  implies: "⇒", impliedby: "⇐",
};

const LOGIC: Record<string, string> = {
  forall: "∀", exists: "∃", nexists: "∄", neg: "¬", lnot: "¬",
  wedge: "∧", land: "∧", vee: "∨", lor: "∨",
  vdash: "⊢", models: "⊨", bot: "⊥", top: "⊤",
};

const SETS: Record<string, string> = {
  emptyset: "∅", aleph: "ℵ",
};

const BIGOPS: Record<string, string> = {
  int: "∫", iint: "∬", iiint: "∭", oint: "∮",
  sum: "∑", prod: "∏", coprod: "∐",
  bigcup: "⋃", bigcap: "⋂",
};

const FNS = new Set([
  "sin", "cos", "tan", "cot", "sec", "csc",
  "arcsin", "arccos", "arctan",
  "sinh", "cosh", "tanh",
  "log", "ln", "exp", "lim", "liminf", "limsup",
  "inf", "sup", "min", "max", "det", "arg", "gcd", "lcm",
]);

// Accent commands → an accent struct over the (recursively-parsed) base.
const ACCENTS = new Set([
  "hat", "widehat", "vec", "overrightarrow", "bar", "overline",
  "tilde", "widetilde", "dot", "ddot", "check", "acute", "grave", "breve",
]);

// Number-set blackboard letters via \mathbb{X}.
const MATHBB: Record<string, string> = {
  N: "ℕ", Z: "ℤ", Q: "ℚ", R: "ℝ", C: "ℂ", P: "𝒫",
};

/**
 * Parse a LaTeX string into a Token list.
 *
 * If `ok=false`, the result still contains best-effort tokens for the
 * parseable prefix; the unparsed remainder is reported via `unparsed`.
 */
export function latexToTokens(input: string): ParseResult {
  // Strip `\,` `\;` `\:` `\!` (spacing macros) and a leading/trailing
  // math-mode delimiter pair (`$ … $`, `$$ … $$`, `\( … \)`, `\[ … \]`).
  // The `\\` (hard line-break) marker is preserved — the tokenizer
  // detects it via `src.startsWith("\\\\", i)`.
  let src = (input ?? "")
    .replace(/\\qquad|\\quad/g, " ")   // wide spacing → plain space (skipped)
    .replace(/\\[,;:!]/g, "")          // thin/med/neg spacing macros
    .replace(/(?<!\\)\\ /g, "")        // `\ ` thin space — NOT the `\\` linebreak
    .trim();
  src = src.replace(/^\$\$?/, "").replace(/\$\$?$/, "").trim();
  src = src.replace(/^\\\(/, "").replace(/\\\)$/, "").trim();
  src = src.replace(/^\\\[/, "").replace(/\\\]$/, "").trim();

  const out: Token[] = [];
  let i = 0;
  let ok = true;
  let firstUnparsed = "";

  // Parse a slot's inner LaTeX recursively into tokens, propagating any
  // parse failure up to this result. Used to fill structure slots
  // (numerators, radicands, scripts, …) so a slot can hold nested
  // structures and commands, not just bare characters.
  const sub = (s: string): Token[] => {
    const r = latexToTokens(s);
    if (!r.ok) { ok = false; if (!firstUnparsed) firstUnparsed = r.unparsed ?? ""; }
    return r.tokens;
  };

  while (i < src.length) {
    const ch = src[i];

    // Whitespace
    if (ch === " " || ch === "\t" || ch === "\n") { i++; continue; }

    // Hard line break `\\`
    if (src.startsWith("\\\\", i)) {
      out.push(T.newline());
      i += 2;
      continue;
    }

    // Alignment marker `&`
    if (ch === "&") { out.push(T.align()); i++; continue; }

    // Brace groups: { … } — recursive parse + concatenation. Used as
    // arguments below; not allowed at top level (treated as group).
    if (ch === "{") {
      const inner = readBraceGroup(src, i);
      const sub = latexToTokens(inner.body);
      out.push(...sub.tokens);
      if (!sub.ok) { ok = false; if (!firstUnparsed) firstUnparsed = sub.unparsed ?? ""; }
      i = inner.end;
      continue;
    }

    // Super/subscript attached to the preceding atom — wrap into a
    // subsup struct. The preceding token becomes the body atom; the
    // sub/sup arg becomes the slot.
    if (ch === "^" || ch === "_") {
      // The preceding token becomes the base slot verbatim (preserving
      // its structure); the script args parse recursively.
      const prev = out.pop();
      const base = prev ? [prev] : [];
      const arg = readArg(src, i + 1);
      // If there's already another sub/sup on the next char, fold it
      // into a single struct.
      let supText = ch === "^" ? arg.body : "";
      let subText = ch === "_" ? arg.body : "";
      let cursor = arg.end;
      if (src[cursor] === (ch === "^" ? "_" : "^")) {
        const arg2 = readArg(src, cursor + 1);
        if (ch === "^") subText = arg2.body; else supText = arg2.body;
        cursor = arg2.end;
      }
      out.push(T.subsup(base, sub(subText), sub(supText)));
      i = cursor;
      continue;
    }

    // Backslash command
    if (ch === "\\") {
      const cmd = readCommand(src, i);
      i = cmd.end;
      const name = cmd.name;

      // \frac{a}{b}
      if (name === "frac" || name === "tfrac" || name === "dfrac") {
        const a = readArg(src, i); i = a.end;
        const b = readArg(src, i); i = b.end;
        out.push(T.frac(sub(a.body), sub(b.body)));
        continue;
      }
      // \binom{n}{k} (and d/tbinom) → binomial-coefficient struct.
      if (name === "binom" || name === "dbinom" || name === "tbinom") {
        const a = readArg(src, i); i = a.end;
        const b = readArg(src, i); i = b.end;
        out.push(T.binom(sub(a.body), sub(b.body)));
        continue;
      }
      // \sqrt[n]{x} or \sqrt{x}
      if (name === "sqrt") {
        if (src[i] === "[") {
          const idx = readBracketGroup(src, i);
          const body = readArg(src, idx.end);
          out.push(T.nthroot(sub(idx.body), sub(body.body)));
          i = body.end;
          continue;
        }
        const a = readArg(src, i);
        out.push(T.sqrt(sub(a.body)));
        i = a.end;
        continue;
      }
      // \begin{pmatrix}…\end{pmatrix} (and b/v/V/plain matrix) → a matrix
      // struct with recursively-parsed cells. Rows split on `\\`, cells on
      // `&`. Other environments (cases/aligned/array/…) aren't modelled
      // yet — keep parsing but flag the result so the host shows source.
      if (name === "begin") {
        const env = readArg(src, i); i = env.end;
        const envName = env.body.trim();
        const endTok = `\\end{${envName}}`;
        const endIdx = src.indexOf(endTok, i);
        if (endIdx < 0) {
          ok = false; if (!firstUnparsed) firstUnparsed = `\\begin{${envName}}`;
          break;
        }
        const inner = src.slice(i, endIdx);
        i = endIdx + endTok.length;
        if (/^[pbvV]?matrix$/.test(envName)) {
          const grid = inner
            .split(/\\\\/)
            .map((r) => r.trim())
            .filter((r) => r.length > 0)
            .map((r) => r.split("&").map((c) => c.trim()));
          const r = grid.length || 1;
          const c = Math.max(1, ...grid.map((row) => row.length));
          const m = T.matrix(r, c);
          for (let ri = 0; ri < r; ri++) {
            for (let ci = 0; ci < c; ci++) m.slots[`r${ri}c${ci}`] = sub(grid[ri]?.[ci] ?? "");
          }
          out.push(m);
          continue;
        }
        ok = false; if (!firstUnparsed) firstUnparsed = `\\begin{${envName}}`;
        continue;
      }
      // \hat{x}, \vec{v}, \bar{x}, \overline{x}, … → accent struct.
      if (ACCENTS.has(name)) {
        const a = readArg(src, i); i = a.end;
        out.push(T.accent(name, sub(a.body)));
        continue;
      }
      // \mathbb{N}, \mathcal{P}, \mathbf, \mathrm — single-letter sets
      // get rendered as the matching blackboard glyph; everything
      // else folds the body in as upright text.
      if (name === "mathbb" || name === "mathcal" || name === "mathbf" || name === "mathrm") {
        const a = readArg(src, i); i = a.end;
        const body = a.body.trim();
        if (name === "mathbb" && body.length === 1 && MATHBB[body]) {
          out.push(T.upletter(MATHBB[body]));
        } else {
          out.push(T.upletter(body));
        }
        continue;
      }
      // \int, \sum, \prod — emit a bigop atom. Subscripts/supers
      // follow via the regular `_{…}^{…}` rule.
      if (BIGOPS[name]) {
        out.push(T.bigop(BIGOPS[name]));
        continue;
      }
      // \left( … \right): wrap the inside in a parens struct, preserving
      // the delimiter kind ( [ \{ \langle, and | for absolute value).
      if (name === "left") {
        let delim: "paren" | "bracket" | "brace" | "angle" = "paren";
        let isAbs = false;
        if (src[i] === "\\") {
          const cmd = readCommand(src, i); i = cmd.end;
          if (cmd.name === "" && src[i] === "{") { delim = "brace"; i += 1; }
          else if (cmd.name === "langle") delim = "angle";
          else if (cmd.name === "lvert" || cmd.name === "lVert" || cmd.name === "vert" || cmd.name === "Vert") isAbs = true;
          else delim = "paren";
        } else {
          const open = src[i] ?? ""; i += 1;
          if (open === "[") delim = "bracket";
          else if (open === "|") isAbs = true;
        }
        const closeMatch = src.indexOf("\\right", i);
        if (closeMatch < 0) {
          ok = false;
          if (!firstUnparsed) firstUnparsed = "\\left";
          break;
        }
        const inner = src.slice(i, closeMatch);
        i = closeMatch + 6; // past "\right"
        // Consume the closing delimiter — a command (\} \rangle \rvert) or
        // a single char (] ) | ).
        if (src[i] === "\\") {
          const cc = readCommand(src, i); i = cc.end;
          if (cc.name === "" && src[i] === "}") i += 1;
        } else if (i < src.length) {
          i += 1;
        }
        out.push(isAbs ? T.abs(sub(inner)) : T.parens(sub(inner), delim));
        continue;
      }
      // \right alone — should have been consumed; treat as noise.
      if (name === "right") continue;
      // \text{…} → drop into upletter
      if (name === "text" || name === "operatorname") {
        const a = readArg(src, i); i = a.end;
        out.push(T.upletter(a.body));
        continue;
      }
      // Ellipses
      if (name === "cdots" || name === "dotsb" || name === "dotsm") { out.push(T.op("⋯")); continue; }
      if (name === "ldots" || name === "dots" || name === "dotsc") { out.push(T.op("…")); continue; }
      if (name === "vdots") { out.push(T.op("⋮")); continue; }
      if (name === "ddots") { out.push(T.op("⋱")); continue; }
      // \ell — script l. Greek-category token so it's a clickable symbol
      // tile (cat-driven) and emits \ell via the GREEK_MAP fallback.
      if (name === "ell") { out.push(T.greek("ℓ")); continue; }
      // Norm ‖·‖ via \lVert … \rVert (and lvert/Vert variants) → abs struct
      // (renders as bars; norm() folds the spelling for round-trip).
      if (name === "lVert" || name === "lvert" || name === "Vert") {
        let endIdx = -1, endLen = 0;
        for (const e of ["\\rVert", "\\rvert", "\\Vert"]) {
          const x = src.indexOf(e, i);
          if (x >= 0 && (endIdx < 0 || x < endIdx)) { endIdx = x; endLen = e.length; }
        }
        if (endIdx < 0) { ok = false; if (!firstUnparsed) firstUnparsed = `\\${name}`; out.push(T.upletter(`\\${name}`)); continue; }
        const inner = src.slice(i, endIdx); i = endIdx + endLen;
        out.push(T.abs(sub(inner)));
        continue;
      }

      // Atom-style commands
      if (GREEK[name])  { out.push(T.greek(GREEK[name])); continue; }
      if (OPS[name])    { out.push(T.op(OPS[name])); continue; }
      if (RELS[name])   { out.push(T.rel(RELS[name])); continue; }
      if (ARROWS[name]) { out.push(T.arrow(ARROWS[name])); continue; }
      if (LOGIC[name])  { out.push(T.op(LOGIC[name])); continue; }
      if (SETS[name])   { out.push(T.upletter(SETS[name])); continue; }
      if (FNS.has(name)) { out.push(T.fn(name)); continue; }

      // Unknown command — surface as upletter to keep something
      // visible, but flag the result as non-OK so the caller knows
      // to fall back to the raw source view.
      ok = false;
      if (!firstUnparsed) firstUnparsed = `\\${name}`;
      out.push(T.upletter(`\\${name}`));
      continue;
    }

    // Plain characters
    if (/[a-zA-Z]/.test(ch)) {
      // Identifier-like: each letter is its own atom (matches existing
      // token-style for the visual editor's caret model).
      out.push(T.letter(ch));
      i++;
      continue;
    }
    if (/[0-9]/.test(ch)) { out.push(T.num(ch)); i++; continue; }

    if ("+-*=".includes(ch)) {
      if (ch === "-") out.push(T.op("−"));
      else if (ch === "*") out.push(T.op("⋅"));
      else if (ch === "=") out.push(T.rel("="));
      else out.push(T.op(ch));
      i++;
      continue;
    }
    if ("<>".includes(ch)) {
      out.push(T.rel(ch));
      i++;
      continue;
    }
    if ("()[],.;:!?'|/".includes(ch)) {
      out.push(T.punct(ch));
      i++;
      continue;
    }

    // Anything else: record as unparsed and continue.
    ok = false;
    if (!firstUnparsed) firstUnparsed = ch;
    out.push(T.punct(ch));
    i++;
  }

  return { tokens: out, ok, unparsed: firstUnparsed || undefined };
}

/** Read a `\name` command starting at `start` (pointing at `\`). */
function readCommand(src: string, start: number): { name: string; end: number } {
  let i = start + 1;
  while (i < src.length && /[a-zA-Z]/.test(src[i])) i++;
  return { name: src.slice(start + 1, i), end: i };
}

/** Read a `{ … }` or single-token arg. Returns body + position past
 *  the closing brace. */
function readArg(src: string, start: number): { body: string; end: number } {
  if (src[start] === "{") {
    const g = readBraceGroup(src, start);
    return { body: g.body, end: g.end };
  }
  // No brace — take a single character (or a `\command`).
  if (src[start] === "\\") {
    const cmd = readCommand(src, start);
    return { body: src.slice(start, cmd.end), end: cmd.end };
  }
  return { body: src[start] ?? "", end: start + 1 };
}

function readBraceGroup(src: string, start: number): { body: string; end: number } {
  // src[start] === '{'
  let depth = 1;
  let i = start + 1;
  while (i < src.length && depth > 0) {
    if (src[i] === "{") depth++;
    else if (src[i] === "}") depth--;
    if (depth === 0) break;
    i++;
  }
  return { body: src.slice(start + 1, i), end: i + 1 };
}

function readBracketGroup(src: string, start: number): { body: string; end: number } {
  // src[start] === '['
  let depth = 1;
  let i = start + 1;
  while (i < src.length && depth > 0) {
    if (src[i] === "[") depth++;
    else if (src[i] === "]") depth--;
    if (depth === 0) break;
    i++;
  }
  return { body: src.slice(start + 1, i), end: i + 1 };
}
